import React from "react";
import { Form } from "react-final-form";
import { Box, Button, Typography } from "@mui/material";

import { LoadingButton } from "ui";

import { getAllAcceptedExtensions, validateFilePackage } from "fond/fileValidation";
import { DropzoneField } from "fond/form/fields";
import { FileList } from "fond/project/panels/ISG/InputPanel/FileList";
import { getFilesFromEvent } from "fond/utils/files";
import { Modal } from "fond/widgets";

interface UploadModalProps {
  layerTitle: string;
  onClose: () => void;
  onSubmit: (files: File[]) => void;
}

interface UploadFormData {
  Files: File[];
}

const validateFiles = (files: File[] | undefined) => {
  if (!files || files.length === 0) {
    return "Please select files to upload";
  }

  const { isPackageValid, error } = validateFilePackage(files);
  if (!isPackageValid && error) {
    return error.message;
  }
  if (!isPackageValid) {
    return "Please upload a valid GeoJSON file or shapefile package (.shp, .dbf, .prj)";
  }

  return undefined;
};

const UploadModal: React.FC<UploadModalProps> = ({ layerTitle, onClose, onSubmit }: UploadModalProps) => {
  const handleSubmit = (values: UploadFormData) => {
    onSubmit(values.Files);
  };

  return (
    <Modal
      open
      variant="primary"
      header={`Upload ${layerTitle}`}
      content={
        <Box mt={2}>
          <Form<UploadFormData>
            validateOnBlur
            onSubmit={handleSubmit}
            subscription={{ submitting: true, valid: true, dirty: true }}
            render={({ handleSubmit, submitting, valid, dirty }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Box my={2} display="flex" flexDirection="column" rowGap={3}>
                    <Typography variant="body2" color="textSecondary">
                      Upload a GeoJSON file or shapefile package for the {layerTitle.toLowerCase()} layer.
                    </Typography>
                    <DropzoneField
                      name="Files"
                      acceptedFiles={getAllAcceptedExtensions()}
                      fullWidth
                      message="Drop files here or click to browse."
                      getFilesFromEvent={getFilesFromEvent}
                      validate={validateFiles}
                    />
                    <FileList />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                      py: 3,
                    }}
                  >
                    <Button type="button" color="primary" onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
                      Cancel
                    </Button>
                    <LoadingButton type="submit" color="primary" loading={submitting} disabled={!dirty || !valid || submitting}>
                      Upload
                    </LoadingButton>
                  </Box>
                </form>
              );
            }}
          />
        </Box>
      }
      onClose={onClose}
      DialogActionsProps={{ sx: { display: "none" } }}
    />
  );
};

export default UploadModal;
